import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE } from '../config';

interface VersionData {
  platform: 'android' | 'ios';
  version: string;
  minRequiredVersion: string;
  updateUrl: string;
  updateMessage: string;
  isForceUpdate: boolean;
}

const AppVersionManagement: React.FC = () => {
  const [versions, setVersions] = useState<VersionData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<VersionData>({
    platform: 'android',
    version: '',
    minRequiredVersion: '',
    updateUrl: '',
    updateMessage: 'Please update to the latest version to continue using the app.',
    isForceUpdate: false
  });

  // Form validation
  const [formErrors, setFormErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    fetchVersions();
  }, []);

  const fetchVersions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/api/appVersion/versions`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (response.data.success) {
        setVersions(response.data.data);
      }
    } catch (err) {
      setError('Failed to fetch version data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const validateForm = () => {
    const errors: { [key: string]: string } = {};
    const semverRegex = /^\d+\.\d+\.\d+$/;

    if (!formData.version) {
      errors.version = 'Version is required';
    } else if (!semverRegex.test(formData.version)) {
      errors.version = 'Version must be in format x.y.z';
    }

    if (!formData.minRequiredVersion) {
      errors.minRequiredVersion = 'Minimum version is required';
    } else if (!semverRegex.test(formData.minRequiredVersion)) {
      errors.minRequiredVersion = 'Version must be in format x.y.z';
    }

    if (!formData.updateUrl) {
      errors.updateUrl = 'Update URL is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;

    if (type === 'checkbox') {
      const checkbox = e.target as HTMLInputElement;
      setFormData({
        ...formData,
        [name]: checkbox.checked
      });
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccessMessage(null);

      const response = await axios.post(`${API_BASE}/api/appVersion/versions`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`
        }
      });

      if (response.data.success) {
        setSuccessMessage(`Version updated successfully for ${formData.platform}`);
        fetchVersions();
      }
    } catch (err) {
      setError('Failed to update version');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      platform: 'android',
      version: '',
      minRequiredVersion: '',
      updateUrl: '',
      updateMessage: 'Please update to the latest version to continue using the app.',
      isForceUpdate: false
    });
    setFormErrors({});
  };

  const editVersion = (version: VersionData) => {
    setFormData({
      platform: version.platform,
      version: version.version,
      minRequiredVersion: version.minRequiredVersion,
      updateUrl: version.updateUrl,
      updateMessage: version.updateMessage,
      isForceUpdate: version.isForceUpdate
    });
  };

  return (
    <div className="min-h-screen bg-gray-50/50 p-4 md:p-8">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-xl md:text-3xl font-black text-gray-900 mb-6 md:mb-10 uppercase tracking-tight">App Version Management</h1>

        {error && <div className="bg-red-50 border border-red-100 p-4 mb-6 rounded-xl text-red-700 text-sm font-medium">{error}</div>}
        {successMessage && <div className="bg-green-50 border border-green-100 p-4 mb-6 rounded-xl text-green-700 text-sm font-medium">{successMessage}</div>}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
          {/* Update Form */}
          <div className="order-2 md:order-1">
            <h2 className="text-lg md:text-xl font-bold mb-4 text-gray-800">Update Version</h2>
            <form onSubmit={handleSubmit} className="space-y-4 p-4 md:p-6 bg-white shadow-xl shadow-gray-200/50 rounded-2xl border border-gray-100">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] md:text-xs font-bold text-gray-400 uppercase mb-1">Platform</label>
                  <select
                    name="platform"
                    value={formData.platform}
                    onChange={handleInputChange}
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium"
                  >
                    <option value="android">Android</option>
                    <option value="ios">iOS</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[10px] md:text-xs font-bold text-gray-400 uppercase mb-1">Current Version</label>
                  <input
                    type="text"
                    name="version"
                    value={formData.version}
                    onChange={handleInputChange}
                    placeholder="1.0.0"
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium"
                  />
                  {formErrors.version && <p className="text-red-500 text-[10px] mt-1 font-bold italic">{formErrors.version}</p>}
                </div>

                <div>
                  <label className="block text-[10px] md:text-xs font-bold text-gray-400 uppercase mb-1">Min Required</label>
                  <input
                    type="text"
                    name="minRequiredVersion"
                    value={formData.minRequiredVersion}
                    onChange={handleInputChange}
                    placeholder="1.0.0"
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium"
                  />
                  {formErrors.minRequiredVersion && <p className="text-red-500 text-[10px] mt-1 font-bold italic">{formErrors.minRequiredVersion}</p>}
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-[10px] md:text-xs font-bold text-gray-400 uppercase mb-1">Update URL</label>
                  <input
                    type="text"
                    name="updateUrl"
                    value={formData.updateUrl}
                    onChange={handleInputChange}
                    placeholder="https://play.google.com/..."
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium"
                  />
                  {formErrors.updateUrl && <p className="text-red-500 text-[10px] mt-1 font-bold italic">{formErrors.updateUrl}</p>}
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-[10px] md:text-xs font-bold text-gray-400 uppercase mb-1">Update Message</label>
                  <textarea
                    name="updateMessage"
                    value={formData.updateMessage}
                    onChange={handleInputChange}
                    className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none text-sm font-medium"
                    rows={2}
                  ></textarea>
                </div>
              </div>

              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl border border-blue-100">
                <input
                  type="checkbox"
                  id="isForceUpdate"
                  name="isForceUpdate"
                  checked={formData.isForceUpdate}
                  onChange={handleInputChange}
                  className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="isForceUpdate" className="text-xs md:text-sm font-bold text-blue-700 uppercase tracking-wider">Force Update Required</label>
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  className="flex-1 bg-blue-600 text-white py-3 rounded-xl hover:bg-blue-700 font-bold uppercase text-xs tracking-widest shadow-lg shadow-blue-200 active:scale-95 transition-all"
                  disabled={loading}
                >
                  {loading ? 'Saving...' : 'Save Version'}
                </button>

                <button
                  type="button"
                  onClick={resetForm}
                  className="px-6 py-3 bg-gray-100 text-gray-600 rounded-xl hover:bg-gray-200 font-bold uppercase text-xs tracking-widest transition-all"
                >
                  Reset
                </button>
              </div>
            </form>
          </div>

          {/* Current Versions List */}
          <div className="order-1 md:order-2">
            <h2 className="text-lg md:text-xl font-bold mb-4 text-gray-800">Current Versions</h2>

            {loading && versions.length === 0 ? (
              <div className="flex justify-center py-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : versions.length > 0 ? (
              <div className="grid grid-cols-1 gap-4">
                {versions.map((version, index) => (
                  <div key={index} className="p-4 md:p-6 bg-white border border-gray-100 rounded-2xl shadow-lg shadow-gray-200/40 relative group overflow-hidden">
                    <div className="absolute top-0 right-0 w-16 h-16 bg-blue-50 rounded-bl-[40px] -mr-4 -mt-4 transition-all group-hover:w-20 group-hover:h-20"></div>

                    <div className="flex justify-between items-start mb-4 relative z-10">
                      <div>
                        <span className={`px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter ${version.platform === 'android' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                          }`}>
                          {version.platform}
                        </span>
                      </div>
                      <button
                        onClick={() => editVersion(version)}
                        className="text-blue-600 text-[10px] font-black uppercase tracking-widest hover:bg-blue-50 px-3 py-1.5 rounded-lg border border-transparent hover:border-blue-100 transition-all"
                      >
                        Edit Details
                      </button>
                    </div>

                    <div className="grid grid-cols-2 gap-4 text-xs relative z-10">
                      <div>
                        <span className="block text-[9px] text-gray-400 font-bold uppercase mb-1">Current Version</span>
                        <span className="text-gray-900 font-black text-sm">{version.version}</span>
                      </div>
                      <div>
                        <span className="block text-[9px] text-gray-400 font-bold uppercase mb-1">Min Required</span>
                        <span className="text-gray-900 font-black text-sm">{version.minRequiredVersion}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="block text-[9px] text-gray-400 font-bold uppercase mb-1">Store Link</span>
                        <a href={version.updateUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 font-medium truncate block bg-blue-50/50 p-2 rounded-lg border border-blue-50 hover:bg-blue-100/50 transition-colors">
                          {version.updateUrl}
                        </a>
                      </div>
                      <div className="col-span-2 flex items-center gap-2 mt-1">
                        <span className="text-[9px] text-gray-400 font-bold uppercase">Force Update:</span>
                        <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase ${version.isForceUpdate ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'
                          }`}>
                          {version.isForceUpdate ? 'Enabled' : 'Disabled'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-10 border-2 border-dashed border-gray-200 rounded-2xl text-center">
                <p className="text-gray-400 text-sm font-medium italic">No version data available.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AppVersionManagement;